import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Info, Palette, Wand2, Image, Bookmark, LayoutGrid, Sparkles } from 'lucide-react';

const OnboardingOverlay = ({ isActive, onClose }) => {
    const [currentStep, setCurrentStep] = useState(0);

    const steps = [
        {
            id: 'welcome',
            title: 'Welcome to Komorebi',
            description: 'A professional color palette generator with 14+ industry standards, accessibility checking, and powerful export options. Let\'s take a quick tour of the main features!',
            selector: '.fixed.left-0.top-0.bottom-0',
            icon: <Sparkles className="w-5 h-5" />
        },
        {
            id: 'color-info',
            title: 'Color Information',
            description: 'View detailed color information, color history, and explore your selected color with the picker. All your recent colors are saved here for easy access. Right-click any color to add it to your custom palette.',
            selector: 'button[aria-label="Color Information"]',
            icon: <Info className="w-5 h-5" />
        },
        {
            id: 'palettes',
            title: 'Generated Palettes',
            description: 'Auto-generated harmony-based palettes from your selected color. Explore Monochromatic, Analogous, Complementary, Triad, Tetrad, and Split-Complementary color schemes. Palettes update in real-time as you adjust colors.',
            selector: 'button[aria-label="Generated Palettes"]',
            icon: <Palette className="w-5 h-5" />
        },
        {
            id: 'builder',
            title: 'Palette Builder',
            description: 'Build custom palettes from scratch or use color standards like RAL, Pantone, HKS, NCS, and more. Perfect for matching physical materials or creating brand-specific palettes.',
            selector: 'button[aria-label="Palette Builder"]',
            icon: <Wand2 className="w-5 h-5" />
        },
        {
            id: 'extractor',
            title: 'Image Extractor',
            description: 'Upload any image and extract its dominant colors instantly. Drag and drop or browse to upload. Great for creating palettes from photos, artwork, or inspiration images.',
            selector: 'button[aria-label="Image Extractor"]',
            icon: <Image className="w-5 h-5" />
        },
        {
            id: 'saved',
            title: 'Saved Palettes',
            description: 'Manage all your saved color palettes in one place. Search by name or color, rename palettes inline, and export to various formats. Your custom palette from the toolbar is also shown here.',
            selector: 'button[aria-label="Saved Palettes"]',
            icon: <Bookmark className="w-5 h-5" />
        },
        {
            id: 'visualizer',
            title: 'Color Visualizer',
            description: 'Preview your colors in real UI components like buttons, cards, forms, and dashboards. Load saved palettes, drag to reorder colors, and export to CSS, SVG, or palette formats.',
            selector: 'button[aria-label="Visualizer"]',
            icon: <LayoutGrid className="w-5 h-5" />
        },
        {
            id: 'toolbar',
            title: 'Bottom Toolbar',
            description: 'Quick access to essential tools: randomize colors, pick colors, use eye dropper, toggle dark mode, control field visibility, simulate color blindness, manage custom palettes, and view help.',
            selector: '[data-toolbar]',
            icon: <Sparkles className="w-5 h-5" />
        }
    ];

    const getElementRect = (selector) => {
        const element = document.querySelector(selector);
        if (element) {
            return element.getBoundingClientRect();
        }
        return null;
    };

    const getSpotlightPosition = (step) => {
        const rect = getElementRect(step.selector);
        if (!rect && step.fallbackSelector) {
            const fallbackRect = getElementRect(step.fallbackSelector);
            if (fallbackRect) {
                return {
                    top: fallbackRect.top - 8,
                    left: fallbackRect.left - 8,
                    width: fallbackRect.width + 16,
                    height: fallbackRect.height + 16
                };
            }
        }

        if (rect) {
            // Special handling for activity bar buttons - expand spotlight area
            if (step.id !== 'welcome' && step.id !== 'toolbar') {
                return {
                    top: rect.top - 8,
                    left: rect.left - 8,
                    width: rect.width + 16,
                    height: rect.height + 16
                };
            }
            return {
                top: rect.top - 8,
                left: rect.left - 8,
                width: rect.width + 16,
                height: rect.height + 16
            };
        }

        // Fallback positions
        const fallbackPositions = {
            'welcome': { top: 0, left: 0, width: 64, height: window.innerHeight },
            'toolbar': { top: window.innerHeight - 80, left: window.innerWidth / 2 - 250, width: 500, height: 60 },
            'color-info': { top: 100, left: 0, width: 64, height: 60 },
            'palettes': { top: 180, left: 0, width: 64, height: 60 },
            'builder': { top: 260, left: 0, width: 64, height: 60 },
            'extractor': { top: 340, left: 0, width: 64, height: 60 },
            'saved': { top: 420, left: 0, width: 64, height: 60 },
            'visualizer': { top: 500, left: 0, width: 64, height: 60 }
        };

        return fallbackPositions[step.id] || { top: 200, left: 0, width: 64, height: 60 };
    };

    const nextStep = useCallback(() => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            onClose();
        }
    }, [currentStep, steps.length, onClose]);

    const prevStep = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    }, [currentStep]);

    const skipOnboarding = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleKeyDown = useCallback((e) => {
        if (!isActive) return;

        switch (e.key) {
            case 'Escape':
                onClose();
                break;
            case 'ArrowRight':
            case ' ':
                e.preventDefault();
                nextStep();
                break;
            case 'ArrowLeft':
                e.preventDefault();
                prevStep();
                break;
            default:
                break;
        }
    }, [isActive, onClose, nextStep, prevStep]);

    useEffect(() => {
        if (isActive) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'auto';
        };
    }, [handleKeyDown, isActive]);

    if (!isActive) return null;

    const currentStepData = steps[currentStep];
    const spotlightPosition = getSpotlightPosition(currentStepData);

    return (
        <div className="fixed inset-0 z-[9999]">
            {/* Dark overlay with precise cutout */}
            <div
                className="absolute inset-0 bg-black bg-opacity-75 transition-all duration-300"
                style={{
                    clipPath: `polygon(0% 0%, 0% 100%, ${spotlightPosition.left}px 100%, ${spotlightPosition.left}px ${spotlightPosition.top}px, ${spotlightPosition.left + spotlightPosition.width}px ${spotlightPosition.top}px, ${spotlightPosition.left + spotlightPosition.width}px ${spotlightPosition.top + spotlightPosition.height}px, ${spotlightPosition.left}px ${spotlightPosition.top + spotlightPosition.height}px, ${spotlightPosition.left}px 100%, 100% 100%, 100% 0%)`
                }}
            />

            {/* Spotlight border outline */}
            <div
                className="absolute border-2 border-blue-400 rounded-lg transition-all duration-300 ease-in-out pointer-events-none"
                style={{
                    top: spotlightPosition.top - 2,
                    left: spotlightPosition.left - 2,
                    width: spotlightPosition.width + 4,
                    height: spotlightPosition.height + 4,
                    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.3), 0 0 20px rgba(59, 130, 246, 0.4)',
                }}
            />

            {/* Instruction Card */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden z-[10000] border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-6 pb-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                {currentStepData.icon}
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Step {currentStep + 1} of {steps.length}</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Interactive Tutorial</p>
                            </div>
                        </div>
                        <button
                            onClick={skipOnboarding}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            aria-label="Close onboarding"
                        >
                            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                        </button>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{currentStepData.title}</h2>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                        {currentStepData.description}
                    </p>

                    {/* Progress Bar */}
                    <div className="mb-6">
                        <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mb-2">
                            <span>Progress</span>
                            <span>{currentStep + 1} / {steps.length}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Navigation */}
                    <div className="flex items-center justify-between">
                        <button
                            onClick={prevStep}
                            disabled={currentStep === 0}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${currentStep === 0
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                                }`}
                        >
                            <ChevronLeft className="w-4 h-4" />
                            Previous
                        </button>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={skipOnboarding}
                                className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-lg"
                            >
                                Skip Tour
                            </button>
                            <button
                                onClick={nextStep}
                                className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Keyboard hints */}
                <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 border-t border-gray-200 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400">
                    Use <kbd className="inline-flex items-center px-1.5 py-0.5 bg-white dark:bg-gray-600 rounded border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 font-mono">←</kbd> <kbd className="inline-flex items-center px-1.5 py-0.5 bg-white dark:bg-gray-600 rounded border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 font-mono">→</kbd> to navigate, <kbd className="inline-flex items-center px-1.5 py-0.5 bg-white dark:bg-gray-600 rounded border border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-200 font-mono">Esc</kbd> to exit
                </div>
            </div>
        </div>
    );
};

export default OnboardingOverlay;